import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { UnifiedTask } from "../../services";
import {
  TaskCard,
  type TaskCardRowPadding,
  type TaskChipDisplay,
} from "./TaskCard";

type Props = {
  tasks: UnifiedTask[];
  highlightedDeviceId?: string;
  fallbackDeviceId?: string;
  hasGroupPadding?: boolean;
  showGroupHeadings?: boolean;
  taskHorizontalPadding?: number;
  taskRowPadding?: TaskCardRowPadding;
  taskChipDisplay?: TaskChipDisplay;
  dataTestId?: string;
  preserveTaskOrder?: boolean;
};

const compareByCreatedAtDesc = (a: UnifiedTask, b: UnifiedTask) => {
  const timeA = new Date(a.createdAt).getTime();
  const timeB = new Date(b.createdAt).getTime();

  if (Number.isFinite(timeA) && Number.isFinite(timeB) && timeA !== timeB) {
    return timeB - timeA;
  }

  if (a.title !== b.title) {
    return a.title.localeCompare(b.title);
  }

  return a.id.localeCompare(b.id);
};

export const TaskList = ({
  tasks,
  highlightedDeviceId,
  fallbackDeviceId,
  hasGroupPadding = true,
  showGroupHeadings = true,
  taskHorizontalPadding,
  taskRowPadding,
  taskChipDisplay,
  dataTestId,
  preserveTaskOrder = false,
}: Props) => {
  // Group tasks by device
  const groupedByDevice = tasks.reduce(
    (acc, task) => {
      const deviceKey = task.device?.id || "no-device";
      const deviceName = task.device?.name || "Organization Tasks";
      if (!acc[deviceKey]) {
        acc[deviceKey] = { name: deviceName, tasks: [] };
      }
      acc[deviceKey].tasks.push(task);
      return acc;
    },
    {} as Record<string, { name: string; tasks: UnifiedTask[] }>
  );

  const deviceGroups = useMemo(() => {
    const entries = Object.entries(groupedByDevice);
    const sortedEntries = entries.map(
      ([deviceId, group]) =>
        [
          deviceId,
          {
            ...group,
            tasks: preserveTaskOrder
              ? group.tasks
              : [...group.tasks].sort(compareByCreatedAtDesc),
          },
        ] as const
    );

    if (!highlightedDeviceId) {
      return sortedEntries;
    }

    return sortedEntries.sort(([aKey], [bKey]) => {
      if (aKey === highlightedDeviceId) return -1;
      if (bKey === highlightedDeviceId) return 1;
      return 0;
    });
  }, [groupedByDevice, highlightedDeviceId, preserveTaskOrder]);

  const deviceGroupClassName = [
    "flex flex-col gap-2",
    hasGroupPadding ? "py-2 px-3" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const flatTasks = useMemo(
    () => (preserveTaskOrder ? tasks : [...tasks].sort(compareByCreatedAtDesc)),
    [preserveTaskOrder, tasks]
  );
  const renderTaskCard = (task: UnifiedTask) => (
    <TaskCard
      key={task.id}
      task={task}
      fallbackDeviceId={fallbackDeviceId}
      horizontalPadding={taskHorizontalPadding}
      rowPadding={taskRowPadding}
      taskChipDisplay={taskChipDisplay}
    />
  );

  if (!showGroupHeadings) {
    return (
      <Box className="flex flex-col gap-0" data-testid={dataTestId}>
        {flatTasks.map(renderTaskCard)}
      </Box>
    );
  }

  return (
    <Box className="flex flex-col gap-4" data-testid={dataTestId}>
      {deviceGroups.map(([deviceId, group]) => (
        <Box key={deviceId} className={deviceGroupClassName}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            className="uppercase tracking-wider"
          >
            {group.name}
          </Typography>
          <Box className="flex flex-col gap-0">
            {group.tasks.map(renderTaskCard)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
